/**
 * Landing Sections — 共通型定義
 */
import type { ComponentType } from 'react';
import type { ZodTypeAny } from 'zod';

/**
 * セクション単位の表示スタイル設定（@goenhub/shared の SectionStyle と同期）。
 * variant 切替とは別軸で、CTA配置などの細かい挙動を制御する。
 */
export interface SectionStyle {
  /** CTA ボタン整列（hero / cta_banner で有効） */
  ctaAlign?: 'left' | 'center' | 'right';
  /**
   * ロゴ表示位置（Hero セクションで有効）。
   * 'none' で非表示。'top-*' は Hero 上部 inline 配置、'bottom-*' は Hero 内オーバーレイ。
   */
  logoPosition?:
    | 'none'
    | 'top-left'
    | 'top-center'
    | 'top-right'
    | 'bottom-left'
    | 'bottom-right';
  /** ロゴサイズ（Hero セクションで有効） */
  logoSize?: 'sm' | 'md' | 'lg';
}

/**
 * ランディング全体のブランド情報（セクションコンポーネントへ伝達）。
 * Footer の SNS リンクや Hero のロゴ画像などに使う。
 */
export interface BrandContext {
  logoUrl?: string | null;
  socialLinks?: Record<string, string> | null;
}

/** セクションタイプ */
export type SectionType =
  | 'hero'
  | 'social_proof'
  | 'stats'
  | 'product_showcase'
  | 'video'
  | 'image'
  | 'feature_grid'
  | 'bento'
  | 'how_it_works'
  | 'testimonials'
  | 'pricing'
  | 'comparison'
  | 'faq'
  | 'cta_banner'
  | 'newsletter'
  | 'team'
  | 'speaker'
  | 'location'
  | 'countdown'
  | 'feature_spotlight'
  | 'footer';

export const SECTION_TYPES: SectionType[] = [
  'hero',
  'social_proof',
  'stats',
  'product_showcase',
  'video',
  'image',
  'feature_grid',
  'feature_spotlight',
  'bento',
  'how_it_works',
  'testimonials',
  'pricing',
  'comparison',
  'faq',
  'cta_banner',
  'newsletter',
  'team',
  'speaker',
  'location',
  'countdown',
  'footer',
];

/** 10 visual identities */
export type VisualIdentity =
  | 'light'
  | 'dark'
  | 'vibrant'
  | 'pastel'
  | 'editorial'
  | 'mono'
  | 'mint'
  | 'azure'
  | 'terra'
  | 'noir';

export const VISUAL_IDENTITIES: VisualIdentity[] = [
  'light',
  'dark',
  'vibrant',
  'pastel',
  'editorial',
  'mono',
  'mint',
  'azure',
  'terra',
  'noir',
];

/** 業界レシピ */
export type IndustryRecipe =
  | 'saas_b2b'
  | 'consumer_app'
  | 'ecommerce_dtc'
  | 'healthcare_wellness'
  | 'hardware_lineup'
  | 'software_lineup'
  | 'event_promotion';

export const INDUSTRY_RECIPES: IndustryRecipe[] = [
  'saas_b2b',
  'consumer_app',
  'ecommerce_dtc',
  'healthcare_wellness',
  'hardware_lineup',
  'software_lineup',
  'event_promotion',
];

/** AI 生成用 schema field の型 */
export type AISchemaFieldType =
  | 'string'
  | 'url'
  | 'image_url'
  | 'array<string>'
  | 'array<image_url>'
  | 'array<object>';

export interface AISchemaField {
  name: string;
  type: AISchemaFieldType;
  required: boolean;
  max?: number;
  example?: unknown;
  hint?: string;
  /** array<object> の場合のサブフィールド */
  fields?: AISchemaField[];
}

/** UI 編集用 field 型（FieldRenderer のためのマッピング） */
export type UIFieldType =
  | 'text'
  | 'textarea'
  | 'image'
  | 'image_list'
  | 'cta'
  | 'cta_url'
  | 'select'
  | 'datetime'
  | 'product_list'
  | 'feature_list'
  | 'qa_list'
  | 'footer_columns'
  | 'testimonial_list'
  | 'plan_list'
  | 'video_list'
  | 'team_list'
  | 'stat_list'
  | 'step_list'
  | 'logo_list'
  | 'spotlight_blocks'
  | 'comparison_rows';

export interface UISchemaField {
  name: string;
  type: UIFieldType;
  label: string;
  required?: boolean;
  max?: number;
  placeholder?: string;
  hint?: string;
  /** select 型のときの選択肢 */
  options?: Array<{ value: string; label: string }>;
}

/** Variant 定義 */
export interface VariantDefinition<TContent = unknown> {
  name: string;
  description: string;
  Component: ComponentType<{ content: TContent; identity?: VisualIdentity }>;
  /** モック / サンプルデータ */
  sample: TContent;
}

/** Section type 定義 */
export interface SectionDefinition<TContent = unknown> {
  type: SectionType;
  /** 表示用ラベル */
  label: string;
  /** AI 生成用フィールド定義 */
  aiSchema: AISchemaField[];
  /** UI 編集用フィールド定義 */
  uiSchema: UISchemaField[];
  /** Zod runtime validator */
  zodSchema: ZodTypeAny;
  /** バリアントマップ */
  variants: Record<string, VariantDefinition<TContent>>;
}

/** ASSEMBLY recipe step */
export interface RecipeStep {
  type: SectionType;
  variant: string;
  reason: string;
}

/** ASSEMBLY recipe */
export interface RecipeDefinition {
  name: string;
  description: string;
  sequence: RecipeStep[];
  visualIdentityDefault: VisualIdentity;
  rationale: string;
}

/** CONTEXT_RULES */
export interface IdentityRule {
  primary: VisualIdentity;
  alts: VisualIdentity[];
  avoid: VisualIdentity[];
  reason: string;
}

export interface ContextRules {
  identityByIndustry: Record<IndustryRecipe, IdentityRule>;
}

/** 共通の section content props */
export interface SectionRenderProps<T = unknown> {
  content: T;
  identity?: VisualIdentity;
}
